import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import { 
  Package, Search, Plus, Edit2, Trash2, Eye, Share2, FileText,
  Truck, Clock, CheckCircle, AlertTriangle, XCircle, Send,
  ChevronDown, ChevronUp, Upload, Download, Building2, MapPin,
  Calendar, DollarSign, ShoppingCart, Link, Copy, ExternalLink,
  User, Phone, UserPlus, ClipboardCheck, CloudUpload, Loader2, History,
  MoreHorizontal, CreditCard, RefreshCw, Camera, Printer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import ImageLightbox from '@/components/ImageLightbox';
import { DateInput, TimeInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PO_TYPES = [
  { value: "made_to_order", label: "Made to Order" },
  { value: "stock_refill", label: "Stock Refill" },
  { value: "replacement", label: "Replacement" },
  { value: "raw_material", label: "Raw Material" }
];

const PO_STATUSES = [
  { value: "draft", label: "Draft", color: "bg-gray-500/20 text-gray-400" },
  { value: "sent", label: "Sent", color: "bg-blue-500/20 text-blue-400" },
  { value: "confirmed", label: "Confirmed", color: "bg-purple-500/20 text-purple-400" },
  { value: "partially_received", label: "Partial", color: "bg-orange-500/20 text-orange-400" },
  { value: "received", label: "Received", color: "bg-green-500/20 text-green-400" },
  { value: "completed", label: "Completed", color: "bg-green-600/20 text-green-500" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-500/20 text-red-400" }
];

const FULFILLMENT_METHODS = [
  { value: "vendor_delivers", label: "Vendor Delivers" },
  { value: "wooden_twist_pickup", label: "Wooden Twist Pickup" }
];

const PICKUP_STATUSES = [
  { value: "assigned", label: "Assigned", color: "bg-blue-500/20 text-blue-400" },
  { value: "on_the_way", label: "On the Way", color: "bg-orange-500/20 text-orange-400" },
  { value: "reached_vendor", label: "At Vendor", color: "bg-purple-500/20 text-purple-400" },
  { value: "picked", label: "Picked", color: "bg-green-500/20 text-green-400" },
  { value: "delivered_to_warehouse", label: "Delivered", color: "bg-teal-500/20 text-teal-400" },
  { value: "completed", label: "Completed", color: "bg-gray-500/20 text-gray-400" }
];

const PAYMENT_STATUSES = [
  { value: "unpaid", label: "Unpaid", color: "bg-red-500/20 text-red-400" },
  { value: "partial", label: "Partial", color: "bg-orange-500/20 text-orange-400" },
  { value: "paid", label: "Paid", color: "bg-green-500/20 text-green-400" },
  { value: "credit", label: "Credit", color: "bg-blue-500/20 text-blue-400" }
];

const variantLabel = (v) => (v && !/^default( title)?$/i.test(String(v).trim()) ? v : null);

const PurchaseOrdersPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.roles?.includes('admin') || user?.is_super_admin;
  const { formatCurrency } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [summary, setSummary] = useState({ total_count: 0, total_value: 0, pending_count: 0 });
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    draft: 0,
    active: 0,
    pending_payment: 0,
    pickup_pending: 0,
    grn_pending: 0,
    completed: 0,
    cancelled: 0
  });
  
  // Payment history state
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Data for dropdowns
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [pickupAssignDialogOpen, setPickupAssignDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [editingPO, setEditingPO] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Edit modal product search state
  const [editSkuSearch, setEditSkuSearch] = useState('');
  const [editSearchResults, setEditSearchResults] = useState([]);
  const [editSearching, setEditSearching] = useState(false);
  const [addingProductIndex, setAddingProductIndex] = useState(null);
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Pickup assignment form
  const [pickupForm, setPickupForm] = useState({
    driver_id: '',
    driver_name: '',
    pickup_date: '',
    pickup_time: '',
    pickup_notes: ''
  });
  
  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    po_type: 'stock_refill',
    fulfillment_method: 'vendor_delivers',
    warehouse_location_id: '',
    expected_delivery_date: '',
    line_items: [],
    internal_notes: '',
    vendor_notes: '',
    tax_rate: 5,
    tax_inclusive: false
  });
  
  // New line item state
  const [newLineItem, setNewLineItem] = useState({
    sku: '',
    product_title: '',
    quantity: 1,
    unit_cost: 0
  });
  
  // Share link state
  const [shareLink, setShareLink] = useState('');
  
  // Zoho sync state
  const [syncingZoho, setSyncingZoho] = useState(null);
  const [zohoStatus, setZohoStatus] = useState(null);
  
  // Invoice upload state
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '',
    invoice_date: ''
  });
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  
  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);

  // SKU Search state
  const [skuSearch, setSkuSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProductVendorPrices, setSelectedProductVendorPrices] = useState([]);
  const [showPriceComparison, setShowPriceComparison] = useState(false);
  
  // Merge POs state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [selectedPOsForMerge, setSelectedPOsForMerge] = useState([]);
  // Bulk selection state (Feb 2026): drives the "Download as Excel"
  // and any other bulk operations triggered from the PO list table.
  const [selectedPOIds, setSelectedPOIds] = useState(() => new Set());
  const [mergingPOs, setMergingPOs] = useState(false);
  
  // Refresh images state
  const [refreshingImages, setRefreshingImages] = useState(false);

  useEffect(() => {
    loadPurchaseOrders();
    loadVendors();
    loadLocations();
    loadInventory();
    loadDrivers();
    checkZohoStatus();
  }, [vendorFilter]);
  
  // Handle URL parameters to open specific PO
  useEffect(() => {
    const poIdParam = searchParams.get('po');
    const poNumberParam = searchParams.get('poNumber');
    
    if ((poIdParam || poNumberParam) && purchaseOrders.length > 0) {
      let targetPO = null;
      
      if (poIdParam) {
        targetPO = purchaseOrders.find(p => p.id === poIdParam);
      } else if (poNumberParam) {
        targetPO = purchaseOrders.find(p => p.po_number === poNumberParam);
      }
      
      if (targetPO) {
        openViewDialog(targetPO);
        // Clear the URL params after opening
        setSearchParams({});
      }
    }
  }, [purchaseOrders, searchParams]);
  
  const checkZohoStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/zoho/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZohoStatus(response.data);
    } catch (error) {
      console.error('Failed to check Zoho status');
    }
  };
  
  const handleSyncToZoho = async (poId) => {
    setSyncingZoho(poId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/sync-zoho`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Synced to Zoho: ${response.data.zoho_po_number}`);
      loadPurchaseOrders();
    } catch (error) {
      // Surface the full Zoho error (message + per-line details).
      // Backend now returns a multi-line `detail` like:
      //   "Zoho Books error: The given items do not have valid
      //    configurations (Zoho code 4)
      //    • [line_item 0] Tax Treatment is mandatory
      //    • [line_item 1] Item rate must be greater than zero"
      // Show it with preserved line breaks + long duration so the
      // operator can read and act on it.
      const detail = error.response?.data?.detail || 'Failed to sync to Zoho Books';
      toast.error(detail, {
        duration: 15000,
        style: { whiteSpace: 'pre-line', maxWidth: '480px' },
      });
      console.error('[Zoho sync] failed:', detail);
    } finally {
      setSyncingZoho(null);
    }
  };

  const loadAuditLogs = async (poId) => {
    setLoadingAuditLogs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/audit-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to load audit logs');
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  const openViewDialog = (po) => {
    setSelectedPO(po);
    setViewDialogOpen(true);
    loadAuditLogs(po.id);
    loadPaymentHistory(po.id);
  };

  // Open edit dialog with PO data
  const openEditDialog = async (po) => {
    // If vendor_name is missing, try to fetch it from vendors list
    let vendorName = po.vendor_name;
    if (!vendorName && po.vendor_id) {
      const vendor = vendors.find(v => v.id === po.vendor_id);
      vendorName = vendor?.vendor_name || 'Unknown Vendor';
    }
    
    setEditingPO({
      ...po,
      vendor_name: vendorName,
      line_items: po.line_items || []
    });
    setEditDialogOpen(true);
  };

  // Save edited PO
  const handleSaveEdit = async () => {
    if (!editingPO) return;
    
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/enterprise/purchase-orders/${editingPO.id}`,
        {
          vendor_notes: editingPO.vendor_notes,
          internal_notes: editingPO.internal_notes,
          expected_delivery_date: editingPO.expected_delivery_date,
          fulfillment_method: editingPO.fulfillment_method,
          line_items: editingPO.line_items,
          tax_inclusive: editingPO.tax_inclusive || false,
          tax_rate: editingPO.tax_rate || 5
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Purchase order updated');
      setEditDialogOpen(false);
      setEditingPO(null);
      loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update purchase order');
    } finally {
      setSavingEdit(false);
    }
  };

  // Update line item quantity in edit mode
  const updateEditLineItem = (index, field, value) => {
    setEditingPO(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, line_items: newItems };
    });
  };

  // Remove line item in edit mode
  const removeEditLineItem = (index) => {
    setEditingPO(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Search products for edit modal
  const searchProductsForEdit = async (query) => {
    if (!query || query.length < 2) {
      setEditSearchResults([]);
      return;
    }
    
    setEditSearching(true);
    try {
      const token = localStorage.getItem('token');
      const vendorId = editingPO?.vendor_id || '';
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/inventory/search?q=${encodeURIComponent(query)}&vendor_id=${vendorId}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditSearchResults(response.data.items || []);
    } catch (error) {
      console.error('Search failed:', error);
      setEditSearchResults([]);
    } finally {
      setEditSearching(false);
    }
  };

  // Select product from search for edit modal
  const selectProductForEdit = (item, index) => {
    // Get vendor-specific price if available
    let unitCost = item.cost_price || 0;
    
    if (editingPO?.vendor_id && item.selected_vendor_price) {
      unitCost = item.selected_vendor_price.cost_price || unitCost;
    } else if (item.lowest_vendor) {
      unitCost = item.lowest_vendor.cost_price || unitCost;
    }
    
    setEditingPO(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = {
        ...newItems[index],
        sku: item.sku,
        product_title: item.name || item.product_title,
        product_id: item.shopify_product_id,
        variant_id: item.shopify_variant_id,
        image_url: item.image_url,
        unit_cost: unitCost,
        quantity: 1
      };
      return { ...prev, line_items: newItems };
    });
    
    // Clear search
    setEditSkuSearch('');
    setEditSearchResults([]);
    setAddingProductIndex(null);
  };

  // Open payment dialog for a PO
  const openPaymentDialog = (po) => {
    setSelectedPO(po);
    const remainingAmount = (po.total_amount || 0) - (po.paid_amount || 0);
    setPaymentForm({
      amount: remainingAmount > 0 ? remainingAmount.toString() : '',
      payment_mode: 'bank_transfer',
      payment_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: `Payment for ${po.po_number}`
    });
    setPaymentDialogOpen(true);
  };

  // Submit payment for PO
  const handleSubmitPayment = async () => {
    if (!selectedPO || !paymentForm.amount) {
      toast.error('Please enter payment amount');
      return;
    }

    const amount = parseFloat(paymentForm.amount);
    if (amount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    setSavingPayment(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/enterprise/vendors/${selectedPO.vendor_id}/payments`,
        {
          entry_type: 'payment',
          amount: amount,
          payment_mode: paymentForm.payment_mode,
          payment_date: paymentForm.payment_date,
          reference_number: paymentForm.reference_number,
          linked_po_id: selectedPO.id,
          notes: paymentForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setSelectedPO(null);
      setPaymentForm({
        amount: '',
        payment_mode: 'bank_transfer',
        payment_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: ''
      });
      loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (vendorFilter !== 'all') params.append('vendor_id', vendorFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allPOs = response.data.purchase_orders || [];
      
      // Calculate tab counts
      const counts = {
        all: allPOs.length,
        draft: allPOs.filter(po => po.status === 'draft').length,
        active: allPOs.filter(po => ['sent', 'confirmed', 'partially_received'].includes(po.status)).length,
        pending_payment: allPOs.filter(po => 
          po.status !== 'cancelled' && po.status !== 'draft' && 
          (po.payment_status === 'unpaid' || po.payment_status === 'partial' || !po.payment_status)
        ).length,
        pickup_pending: allPOs.filter(po => 
          po.status !== 'cancelled' && po.status !== 'draft' &&
          !['received', 'completed'].includes(po.status) &&
          po.fulfillment_method === 'wooden_twist_pickup' &&
          (!po.pickup_status || po.pickup_status === 'assigned' || po.pickup_status === 'on_the_way' || po.pickup_status === 'reached_vendor')
        ).length,
        grn_pending: allPOs.filter(po => 
          po.status !== 'cancelled' && 
          ['sent', 'confirmed', 'partially_received'].includes(po.status) &&
          (po.pickup_status === 'picked' || po.pickup_status === 'delivered_to_warehouse' || 
           (po.fulfillment_method === 'vendor_delivers' && po.status !== 'received' && po.status !== 'completed'))
        ).length,
        completed: allPOs.filter(po => 
          ['received', 'completed'].includes(po.status) && po.payment_status === 'paid'
        ).length,
        cancelled: allPOs.filter(po => po.status === 'cancelled').length
      };
      setTabCounts(counts);
      
      setPurchaseOrders(allPOs);
      setSummary(response.data.summary || { total_count: 0, total_value: 0, pending_count: 0 });
    } catch (error) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter POs based on active tab
  const getFilteredPOs = () => {
    let filtered = purchaseOrders;
    
    switch (activeTab) {
      case 'draft':
        filtered = purchaseOrders.filter(po => po.status === 'draft');
        break;
      case 'active':
        filtered = purchaseOrders.filter(po => ['sent', 'confirmed', 'partially_received'].includes(po.status));
        break;
      case 'pending_payment':
        filtered = purchaseOrders.filter(po => 
          po.status !== 'cancelled' && po.status !== 'draft' && 
          (po.payment_status === 'unpaid' || po.payment_status === 'partial' || !po.payment_status)
        );
        break;
      case 'pickup_pending':
        filtered = purchaseOrders.filter(po => 
          po.status !== 'cancelled' && po.status !== 'draft' &&
          !['received', 'completed'].includes(po.status) &&
          po.fulfillment_method === 'wooden_twist_pickup' &&
          (!po.pickup_status || po.pickup_status === 'assigned' || po.pickup_status === 'on_the_way' || po.pickup_status === 'reached_vendor')
        );
        break;
      case 'grn_pending':
        filtered = purchaseOrders.filter(po => 
          po.status !== 'cancelled' && 
          ['sent', 'confirmed', 'partially_received'].includes(po.status) &&
          (po.pickup_status === 'picked' || po.pickup_status === 'delivered_to_warehouse' || 
           (po.fulfillment_method === 'vendor_delivers' && po.status !== 'received' && po.status !== 'completed'))
        );
        break;
      case 'completed':
        filtered = purchaseOrders.filter(po => 
          ['received', 'completed'].includes(po.status) && po.payment_status === 'paid'
        );
        break;
      case 'cancelled':
        filtered = purchaseOrders.filter(po => po.status === 'cancelled');
        break;
      default:
        filtered = purchaseOrders;
    }
    
    // Sort: most recently updated first, with unpaid/partial prioritized
    return filtered.sort((a, b) => {
      // First prioritize by payment status (unpaid > partial > paid)
      const paymentPriority = { 'unpaid': 0, 'partial': 1, 'paid': 2 };
      const aPriority = paymentPriority[a.payment_status] ?? 0;
      const bPriority = paymentPriority[b.payment_status] ?? 0;
      if (aPriority !== bPriority) return aPriority - bPriority;
      
      // Then by updated date (most recent first)
      return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    });
  };
  
  // Load payment history for a PO
  const handleSwitchFulfillment = async (po) => {
    const target = po.fulfillment_method === 'wooden_twist_pickup' ? 'vendor_delivers' : 'wooden_twist_pickup';
    const label = target === 'vendor_delivers' ? 'Vendor Delivers' : 'Pickup From Vendor';
    if (!window.confirm(`Switch ${po.po_number} to "${label}"?${target === 'vendor_delivers' ? ' Any pending pickup assignment will be cleared.' : ''}`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/enterprise/purchase-orders/${po.id}/fulfillment-method`,
        { fulfillment_method: target },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Switched to ${label}`);
      loadPurchaseOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch fulfillment method');
    }
  };

  const loadPaymentHistory = async (poId) => {
    setLoadingPayments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(response.data.payments || []);
      setPaymentSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to load payment history');
      setPaymentHistory([]);
      setPaymentSummary(null);
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/vendors?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to load locations');
    }
  };

  const loadInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data.items || []);
    } catch (error) {
      console.error('Failed to load inventory');
    }
  };

  const loadDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Failed to load drivers');
    }
  };

  const handleAssignPickup = async () => {
    if (!pickupForm.driver_id) {
      toast.error('Please select a driver');
      return;
    }
    if (!pickupForm.pickup_date) {
      toast.error('Please select pickup date');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const driver = drivers.find(d => d.id === pickupForm.driver_id);
      
      await axios.post(`${API_BASE_URL}/enterprise/purchase-orders/${selectedPO.id}/assign-pickup`, {
        driver_id: pickupForm.driver_id,
        driver_name: driver?.full_name || driver?.name || 'Driver',
        pickup_date: pickupForm.pickup_date,
        pickup_time: pickupForm.pickup_time,
        pickup_notes: pickupForm.pickup_notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Pickup driver assigned');
      setPickupAssignDialogOpen(false);
      setPickupForm({ driver_id: '', driver_name: '', pickup_date: '', pickup_time: '', pickup_notes: '' });
      loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign pickup');
    }
  };

  const handleUnassignDriver = async (po) => {
    if (!confirm(`Are you sure you want to unassign ${po.pickup_driver_name} from this pickup?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/enterprise/purchase-orders/${po.id}/unassign-driver`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Driver ${po.pickup_driver_name} unassigned`);
      loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign driver');
    }
  };

  const openPickupAssignDialog = (po) => {
    setSelectedPO(po);
    setPickupForm({
      driver_id: po.pickup_driver_id || '',
      driver_name: po.pickup_driver_name || '',
      pickup_date: po.pickup_date || '',
      pickup_time: po.pickup_time || '',
      pickup_notes: po.pickup_notes || ''
    });
    setPickupAssignDialogOpen(true);
  };

  const getPickupStatusBadge = (status) => {
    const statusConfig = PICKUP_STATUSES.find(s => s.value === status);
    if (!statusConfig) return null;
    return (
      <Badge className={`${statusConfig.color} border-0`}>
        {statusConfig.label}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (status) => {
    // If no status or undefined, default to "unpaid"
    const effectiveStatus = status || "unpaid";
    const statusConfig = PAYMENT_STATUSES.find(s => s.value === effectiveStatus);
    if (!statusConfig) {
      // Fallback to unpaid if status not recognized
      const unpaidConfig = PAYMENT_STATUSES.find(s => s.value === "unpaid");
      return (
        <Badge className={`${unpaidConfig.color} border-0`}>
          {unpaidConfig.label}
        </Badge>
      );
    }
    return (
      <Badge className={`${statusConfig.color} border-0`}>
        {statusConfig.label}
      </Badge>
    );
  };

  const handleCreatePO = async () => {
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const vendor = vendors.find(v => v.id === formData.vendor_id);
      const location = locations.find(l => l.id === formData.warehouse_location_id);
      
      const payload = {
        ...formData,
        vendor_name: vendor?.vendor_name,
        warehouse_location_name: location?.name,
        created_by: user?.id,
        created_by_name: user?.full_name
      };
      
      await axios.post(`${API_BASE_URL}/enterprise/purchase-orders`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Purchase order created');
      setCreateDialogOpen(false);
      resetForm();
      loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const handleUpdateStatus = async (poId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/status`, 
        { status: newStatus, updated_by: user?.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      loadPurchaseOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRecalculateTotal = async (poId, taxSettings = null) => {
    try {
      const token = localStorage.getItem('token');
      const payload = taxSettings || {};
      const response = await axios.post(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/recalculate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { old_total, new_total, tax_amount, tax_inclusive, tax_rate } = response.data;
      toast.success(`Total recalculated: ${formatCurrency(old_total)} → ${formatCurrency(new_total)}`);
      loadPurchaseOrders();
      // Update selected PO if it's the same one
      if (selectedPO && selectedPO.id === poId) {
        setSelectedPO(prev => ({ 
          ...prev, 
          total_amount: new_total, 
          subtotal: response.data.subtotal,
          tax_amount,
          tax_inclusive,
          tax_rate
        }));
      }
    } catch (error) {
      toast.error('Failed to recalculate total');
    }
  };

  const handleDeletePO = async (poId) => {
    if (!confirm('Are you sure you want to cancel this purchase order?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/enterprise/purchase-orders/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Purchase order cancelled');
      loadPurchaseOrders();
    } catch (error) {
      toast.error('Failed to cancel PO');
    }
  };

  const handleGenerateShareLink = async (po) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/enterprise/purchase-orders/${po.id}/share`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const baseUrl = window.location.origin;
      setShareLink(`${baseUrl}/po/view/${response.data.share_token}`);
      setSelectedPO(po);
      setShareDialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate share link');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard');
  };

  const handleShareViaWhatsApp = () => {
    const message = `Purchase Order: ${selectedPO?.po_number}\nView details: ${shareLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Purchase Order ${selectedPO?.po_number}`,
          text: `View purchase order details`,
          url: shareLink
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const addLineItem = () => {
    if (!newLineItem.sku || !newLineItem.product_title) {
      toast.error('Please enter SKU and product title');
      return;
    }
    
    const item = {
      ...newLineItem,
      id: Date.now().toString(),
      total_cost: newLineItem.quantity * newLineItem.unit_cost
    };
    
    setFormData({
      ...formData,
      line_items: [...formData.line_items, item]
    });
    
    setNewLineItem({ sku: '', product_title: '', quantity: 1, unit_cost: 0, image_url: '' });
    setShowPriceComparison(false);
    setSelectedProductVendorPrices([]);
  };

  const removeLineItem = (itemId) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.filter(item => item.id !== itemId)
    });
  };

  // Search products by SKU with debounce
  const searchProductsBySKU = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/inventory/search?q=${encodeURIComponent(query)}&vendor_id=${formData.vendor_id}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchResults(response.data.items || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  };

  // Select product from search results with vendor price
  const selectProductFromSearch = (item) => {
    // Get vendor-specific price if available
    let unitCost = item.cost_price || 0;
    
    if (formData.vendor_id && item.selected_vendor_price) {
      unitCost = item.selected_vendor_price.cost_price || unitCost;
    } else if (item.lowest_vendor) {
      unitCost = item.lowest_vendor.cost_price || unitCost;
    }
    
    setNewLineItem({
      sku: item.sku,
      product_title: item.name || item.product_title,
      product_id: item.shopify_product_id,
      variant_id: item.shopify_variant_id,
      image_url: item.image_url,
      quantity: 1,
      unit_cost: unitCost
    });
    
    // Show vendor price comparison
    if (item.vendor_prices && item.vendor_prices.length > 0) {
      setSelectedProductVendorPrices(item.vendor_prices);
      setShowPriceComparison(true);
    } else {
      setSelectedProductVendorPrices([]);
      setShowPriceComparison(false);
    }
    
    setSkuSearch('');
    setSearchResults([]);
  };

  const selectProductFromInventory = (inventoryItem) => {
    setNewLineItem({
      sku: inventoryItem.sku,
      product_title: inventoryItem.product_title || inventoryItem.name,
      variant_title: inventoryItem.variant_title,
      product_id: inventoryItem.shopify_product_id,
      variant_id: inventoryItem.shopify_variant_id,
      image_url: inventoryItem.image_url,
      quantity: 1,
      unit_cost: inventoryItem.cost_price || 0
    });
  };

  // Merge POs
  const handleMergePOs = async () => {
    if (selectedPOsForMerge.length < 2) {
      toast.error('Select at least 2 POs to merge');
      return;
    }
    
    // Validate same vendor
    const vendorIds = [...new Set(selectedPOsForMerge.map(po => po.vendor_id))];
    if (vendorIds.length > 1) {
      toast.error('All POs must be from the same vendor to merge');
      return;
    }
    
    setMergingPOs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/purchase-orders/merge`,
        { po_ids: selectedPOsForMerge.map(po => po.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setMergeDialogOpen(false);
      setSelectedPOsForMerge([]);
      loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge POs');
    } finally {
      setMergingPOs(false);
    }
  };

  const togglePOForMerge = (po) => {
    if (selectedPOsForMerge.find(p => p.id === po.id)) {
      setSelectedPOsForMerge(selectedPOsForMerge.filter(p => p.id !== po.id));
    } else {
      // Only allow draft/sent POs
      if (po.status !== 'draft' && po.status !== 'sent') {
        toast.error('Only Draft or Sent POs can be merged');
        return;
      }
      // Check vendor compatibility
      if (selectedPOsForMerge.length > 0 && selectedPOsForMerge[0].vendor_id !== po.vendor_id) {
        toast.error('All POs must be from the same vendor');
        return;
      }
      setSelectedPOsForMerge([...selectedPOsForMerge, po]);
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      po_type: 'stock_refill',
      fulfillment_method: 'vendor_delivers',
      warehouse_location_id: '',
      expected_delivery_date: '',
      line_items: [],
      internal_notes: '',
      vendor_notes: '',
      tax_rate: 5,
      tax_inclusive: false
    });
    setNewLineItem({ sku: '', product_title: '', quantity: 1, unit_cost: 0 });
    setSkuSearch('');
    setSearchResults([]);
    setSelectedProductVendorPrices([]);
    setShowPriceComparison(false);
  };

  const openInvoiceDialog = (po) => {
    setSelectedPO(po);
    setInvoiceForm({
      invoice_number: po.vendor_invoice_number || '',
      invoice_date: po.vendor_invoice_date || ''
    });
    setInvoiceFile(null);
    setInvoiceDialogOpen(true);
  };

  const handleInvoiceUpload = async () => {
    if (!invoiceFile) {
      toast.error('Please select an invoice file');
      return;
    }
    
    setUploadingInvoice(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', invoiceFile);
      if (invoiceForm.invoice_number) {
        formData.append('invoice_number', invoiceForm.invoice_number);
      }
      if (invoiceForm.invoice_date) {
        formData.append('invoice_date', invoiceForm.invoice_date);
      }
      
      await axios.post(
        `${API_BASE_URL}/enterprise/purchase-orders/${selectedPO.id}/invoice`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Invoice uploaded successfully');
      setInvoiceDialogOpen(false);
      setInvoiceFile(null);
      setInvoiceForm({ invoice_number: '', invoice_date: '' });
      loadPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload invoice');
    } finally {
      setUploadingInvoice(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusObj = PO_STATUSES.find(s => s.value === status);
    return <Badge className={statusObj?.color || 'bg-gray-500/20 text-gray-400'}>{statusObj?.label || status}</Badge>;
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  // ─────────────── Bulk selection helpers (Feb 2026) ───────────────
  const toggleSelectPO = (poId) => {
    setSelectedPOIds((prev) => {
      const next = new Set(prev);
      if (next.has(poId)) next.delete(poId);
      else next.add(poId);
      return next;
    });
  };

  const toggleSelectAll = (poList) => {
    setSelectedPOIds((prev) => {
      const ids = poList.map((p) => p.id);
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedPOIds(new Set());

  const downloadSelectedAsExcel = () => {
    if (selectedPOIds.size === 0) {
      toast.error('Select at least one PO to download');
      return;
    }
    // Derive a readable GRN status from `po.status` so the spreadsheet
    // can be used as a procurement-reconciliation report.
    const grnLabel = (po) => {
      const s = (po.status || '').toLowerCase();
      if (s === 'received' || s === 'completed') return 'Received';
      if (s === 'partially_received') return 'Partially Received';
      if (s === 'grn_submitted') return 'GRN Submitted (pending verification)';
      if (s === 'cancelled') return 'N/A (Cancelled)';
      return 'Not Received';
    };
    const rows = purchaseOrders
      .filter((po) => selectedPOIds.has(po.id))
      .map((po) => {
        const linked = (po.linked_orders || po.linked_order_numbers || [po.linked_order_number])
          .filter(Boolean).join(', ');
        const total = Number(po.total_amount || 0);
        const paid = Number(po.paid_amount || 0);
        const balance = Math.max(total - paid, 0);
        return {
          'PO Number': po.po_number || '',
          'Linked Order(s)': linked || '',
          'PO Date': po.created_at ? new Date(po.created_at).toLocaleDateString() : '',
          'Vendor': po.vendor_name || '',
          'Item Count': po.line_items?.length || 0,
          'Total Amount': total,
          'Amount Paid': paid,
          'Balance Due': balance,
          'Currency': po.currency || 'AED',
          'PO Status': (po.status || '').replace(/_/g, ' '),
          'Payment Status': (po.payment_status || 'unpaid').replace(/_/g, ' '),
          'GRN Status': grnLabel(po),
          'Pickup Status': (po.pickup_status || '—').replace(/_/g, ' '),
          'Fulfilment Method': (po.fulfillment_method || '').replace(/_/g, ' '),
          'Expected Delivery': po.expected_delivery_date
            ? new Date(po.expected_delivery_date).toLocaleDateString() : '',
        };
      });
    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(
        key.length,
        ...rows.map((r) => String(r[key] ?? '').length),
      ) + 2,
    }));
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Purchase Orders');
    const filename = `purchase-orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(`Downloaded ${rows.length} PO${rows.length === 1 ? '' : 's'} to ${filename}`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Purchase Orders</h1>
          <p className="text-wt-text-muted mt-1">Manage vendor purchase orders and procurement</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedPOIds.size > 0 && (
            <>
              <span className="text-sm text-wt-text-muted px-2" data-testid="po-bulk-selected-count">
                {selectedPOIds.size} selected
              </span>
              <Button
                onClick={downloadSelectedAsExcel}
                variant="outline"
                className="border-wt-gold/40 text-wt-gold hover:bg-wt-gold/10"
                data-testid="po-bulk-download-excel"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Excel
              </Button>
              <Button
                onClick={clearSelection}
                variant="ghost"
                className="text-wt-text-muted hover:text-wt-text-primary"
                data-testid="po-bulk-clear-selection"
              >
                Clear
              </Button>
            </>
          )}
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
            <Plus className="h-4 w-4 mr-2" />
            Create PO
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <FileText className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Total POs</p>
              <p className="text-2xl font-bold text-wt-text-primary">{summary.total_count}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <DollarSign className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Total Value</p>
              <p className="text-2xl font-bold text-wt-text-primary">{formatCurrency(summary.total_value)}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-orange-500/30 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Clock className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Pending</p>
              <p className="text-2xl font-bold text-orange-400">{summary.pending_count}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Building2 className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Active Vendors</p>
              <p className="text-2xl font-bold text-wt-text-primary">{vendors.length}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search PO number, vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadPurchaseOrders()}
            className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
          />
        </div>
        
        <Select value={vendorFilter} onValueChange={setVendorFilter}>
          <SelectTrigger className="w-[180px] bg-wt-bg border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Vendor" />
          </SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">
            <SelectItem value="all">All Vendors</SelectItem>
            {vendors.map(v => (
              <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button onClick={loadPurchaseOrders} variant="outline" className="border-wt-border text-wt-text-primary">
          Search
        </Button>
        
        {/* Merge POs Button */}
        <Button 
          onClick={() => setMergeDialogOpen(true)}
          variant="outline" 
          className="border-wt-border text-wt-text-primary"
        >
          <Package className="h-4 w-4 mr-2" />
          Merge POs
        </Button>
      </div>

      {/* Tab-based PO Organization */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto">
          <TabsList className="bg-wt-subtle border border-wt-border p-1 rounded-lg inline-flex gap-1 min-w-max">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg px-3 py-1.5 text-sm whitespace-nowrap"
            >
              All
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.all}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="draft" 
              className="data-[state=active]:bg-gray-500 data-[state=active]:text-white px-3 py-1.5 text-sm whitespace-nowrap"
            >
              Draft
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.draft}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="active" 
              className="data-[state=active]:bg-blue-500 data-[state=active]:text-white px-3 py-1.5 text-sm whitespace-nowrap"
            >
              Active
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.active}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="pending_payment" 
              className="data-[state=active]:bg-orange-500 data-[state=active]:text-white px-3 py-1.5 text-sm whitespace-nowrap"
            >
              Payment
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.pending_payment}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="pickup_pending" 
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white px-3 py-1.5 text-sm whitespace-nowrap"
            >
              Pickup
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.pickup_pending}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="grn_pending" 
              className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white px-3 py-1.5 text-sm whitespace-nowrap"
            >
              GRN
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.grn_pending}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white px-3 py-1.5 text-sm whitespace-nowrap"
            >
              Done
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.completed}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="cancelled" 
              className="data-[state=active]:bg-red-500 data-[state=active]:text-white px-3 py-1.5 text-sm whitespace-nowrap"
            >
              Cancelled
              <Badge className="ml-1.5 bg-wt-border text-wt-text-primary text-xs">{tabCounts.cancelled}</Badge>
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {/* PO List */}
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-wt-subtle border-b border-wt-border">
              <tr>
                <th className="w-10 p-4">
                  {(() => {
                    const visible = getFilteredPOs();
                    const allChecked = visible.length > 0 && visible.every((p) => selectedPOIds.has(p.id));
                    const someChecked = visible.some((p) => selectedPOIds.has(p.id)) && !allChecked;
                    return (
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => { if (el) el.indeterminate = someChecked; }}
                        onChange={() => toggleSelectAll(visible)}
                        className="h-4 w-4 cursor-pointer accent-wt-gold"
                        data-testid="po-select-all"
                        aria-label="Select all visible POs"
                      />
                    );
                  })()}
                </th>
                <th className="text-left p-4 text-wt-text-muted font-medium">PO Number</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Date</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Vendor</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Type</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Items</th>
                <th className="text-right p-4 text-wt-text-muted font-medium">Total</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Status</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Payment</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Pickup</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredPOs().map((po, index) => (
                <tr key={po.id} className={`border-b border-wt-border/50 hover:bg-wt-subtle/50 ${index % 2 === 0 ? 'bg-wt-bg/30' : ''}`}>
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedPOIds.has(po.id)}
                      onChange={() => toggleSelectPO(po.id)}
                      className="h-4 w-4 cursor-pointer accent-wt-gold"
                      data-testid={`po-select-${po.id}`}
                      aria-label={`Select PO ${po.po_number}`}
                    />
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-wt-gold font-semibold">{po.po_number}</p>
                      {/* Show all linked orders - Clickable */}
                      {(po.linked_orders?.length > 0 || po.linked_order_numbers?.length > 0 || po.linked_order_number) && (
                        <div className="flex items-center gap-1 flex-wrap text-xs">
                          <Link className="h-3 w-3 text-wt-text-muted" />
                          <span className="text-wt-text-muted">Orders:</span>
                          {(po.linked_orders || po.linked_order_numbers || [po.linked_order_number]).filter(Boolean).map((orderNum, idx, arr) => (
                            <span key={idx}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/orders?orderNumber=${encodeURIComponent(orderNum)}`);
                                }}
                                className="text-wt-gold hover:text-wt-gold-hover hover:underline"
                              >
                                {orderNum}
                              </button>
                              {idx < arr.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-wt-text-primary text-sm">
                      {po.created_at ? new Date(po.created_at).toLocaleDateString() : '-'}
                    </p>
                    <p className="text-xs text-wt-text-muted">
                      {po.created_at ? new Date(po.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </td>
                  <td className="p-4">
                    <p className="text-wt-text-primary">{po.vendor_name}</p>
                    <p className="text-xs text-wt-text-muted">{po.vendor_code}</p>
                  </td>
                  <td className="p-4">
                    <p className="text-wt-text-primary text-sm">
                      {PO_TYPES.find(t => t.value === po.po_type)?.label || po.po_type}
                    </p>
                    {po.fulfillment_method === 'wooden_twist_pickup' && (
                      <p className="text-xs text-orange-400 flex items-center gap-1">
                        <Truck className="h-3 w-3" /> Pickup Required
                      </p>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-wt-text-primary">{po.line_items?.length || 0}</span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-wt-text-primary font-semibold">{formatCurrency(po.total_amount)}</span>
                  </td>
                  <td className="p-4 text-center">
                    {getStatusBadge(po.status)}
                  </td>
                  <td className="p-4 text-center">
                    {getPaymentStatusBadge(po.payment_status)}
                  </td>
                  <td className="p-4 text-center">
                    {po.fulfillment_method === 'wooden_twist_pickup' ? (
                      po.pickup_driver_id ? (
                        <div className="text-center">
                          {getPickupStatusBadge(po.pickup_status)}
                          <p className="text-xs text-wt-text-muted mt-1">{po.pickup_driver_name}</p>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => openPickupAssignDialog(po)}
                          className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border-0"
                          data-testid={`assign-pickup-${po.id}`}
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          Assign
                        </Button>
                      )
                    ) : (
                      <span className="text-wt-text-muted text-xs">Vendor Delivers</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-2">
                      {/* Zoho badge if synced */}
                      {po.zoho_synced && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs">
                          Zoho
                        </Badge>
                      )}
                      
                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="border-wt-border text-wt-text-primary"
                            data-testid={`actions-${po.id}`}
                          >
                            Actions
                            <ChevronDown className="h-4 w-4 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-wt-paper border-wt-border w-48">
                          {/* View */}
                          <DropdownMenuItem 
                            onClick={() => openViewDialog(po)}
                            className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          
                          {/* Edit - draft/sent for everyone; admins can edit any non-cancelled PO */}
                          {(['draft', 'sent'].includes(po.status) || (isAdmin && po.status !== 'cancelled')) && (
                            <DropdownMenuItem 
                              onClick={() => openEditDialog(po)}
                              className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                              data-testid={`edit-po-${po.id}`}
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit PO{!['draft', 'sent'].includes(po.status) ? ' (Admin)' : ''}
                            </DropdownMenuItem>
                          )}
                          
                          {/* Share Link */}
                          <DropdownMenuItem 
                            onClick={() => handleGenerateShareLink(po)}
                            className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            Share Link
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator className="bg-wt-border" />
                          
                          {/* Send to Vendor - only for draft */}
                          {po.status === 'draft' && (
                            <DropdownMenuItem 
                              onClick={() => handleUpdateStatus(po.id, 'sent')}
                              className="text-blue-400 hover:bg-wt-subtle cursor-pointer"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Send to Vendor
                            </DropdownMenuItem>
                          )}
                          
                          {/* Upload Invoice - for non-draft, non-cancelled */}
                          {po.status !== 'draft' && po.status !== 'cancelled' && (
                            <DropdownMenuItem 
                              onClick={() => openInvoiceDialog(po)}
                              className="text-yellow-400 hover:bg-wt-subtle cursor-pointer"
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              {po.vendor_invoice_files?.length > 0 ? 'View Invoice' : 'Upload Invoice'}
                            </DropdownMenuItem>
                          )}
                          
                          {/* Record Payment - for non-draft, non-cancelled, unpaid/partial POs */}
                          {po.status !== 'draft' && po.status !== 'cancelled' && 
                           (po.payment_status === 'unpaid' || po.payment_status === 'partial' || !po.payment_status) && (
                            <DropdownMenuItem 
                              onClick={() => openPaymentDialog(po)}
                              className="text-green-400 hover:bg-wt-subtle cursor-pointer"
                              data-testid={`record-payment-${po.id}`}
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              Record Payment
                            </DropdownMenuItem>
                          )}
                          
                          {/* Switch fulfillment method (pickup <-> vendor delivers) */}
                          {!['received', 'completed', 'cancelled', 'draft'].includes(po.status) &&
                            !['picked', 'delivered_to_warehouse'].includes(po.pickup_status || '') && (
                            <DropdownMenuItem 
                              onClick={() => handleSwitchFulfillment(po)}
                              className="text-wt-text-secondary hover:bg-wt-subtle cursor-pointer"
                              data-testid={`po-switch-fulfillment-${po.po_number}`}
                            >
                              <Truck className="h-4 w-4 mr-2" />
                              {po.fulfillment_method === 'wooden_twist_pickup' ? 'Switch to Vendor Delivers' : 'Switch to Pickup From Vendor'}
                            </DropdownMenuItem>
                          )}
                          
                          {/* Print 4x6 Labels */}
                          {!['draft', 'cancelled'].includes(po.status) && (
                            <DropdownMenuItem 
                              onClick={() => window.open(`/po-labels/${po.id}`, '_blank')}
                              className="text-amber-400 hover:bg-wt-subtle cursor-pointer"
                              data-testid={`po-print-labels-${po.po_number}`}
                            >
                              <Printer className="h-4 w-4 mr-2" />
                              Print Labels (4x6)
                            </DropdownMenuItem>
                          )}
                          
                          {/* Receive Goods / GRN */}
                          {['sent', 'confirmed', 'partially_received'].includes(po.status) && (
                            <DropdownMenuItem 
                              onClick={() => navigate(`/purchase-orders/${po.id}/grn`)}
                              className="text-green-400 hover:bg-wt-subtle cursor-pointer"
                            >
                              <ClipboardCheck className="h-4 w-4 mr-2" />
                              Receive Goods
                            </DropdownMenuItem>
                          )}
                          
                          {/* Reassign Driver */}
                          {po.fulfillment_method === 'wooden_twist_pickup' && po.pickup_driver_id && (
                            <DropdownMenuItem 
                              onClick={() => openPickupAssignDialog(po)}
                              className="text-orange-400 hover:bg-wt-subtle cursor-pointer"
                            >
                              <User className="h-4 w-4 mr-2" />
                              Reassign Driver
                            </DropdownMenuItem>
                          )}
                          
                          {/* Unassign Driver - only when status is 'assigned' or not started */}
                          {po.fulfillment_method === 'wooden_twist_pickup' && po.pickup_driver_id && 
                           (!po.pickup_status || po.pickup_status === 'assigned') && (
                            <DropdownMenuItem 
                              onClick={() => handleUnassignDriver(po)}
                              className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                            >
                              <UserPlus className="h-4 w-4 mr-2" />
                              Unassign Driver
                            </DropdownMenuItem>
                          )}
                          
                          {/* Sync to Zoho */}
                          {zohoStatus?.configured && !po.zoho_synced && po.status !== 'draft' && po.status !== 'cancelled' && (
                            <DropdownMenuItem 
                              onClick={() => handleSyncToZoho(po.id)}
                              disabled={syncingZoho === po.id}
                              className="text-purple-400 hover:bg-wt-subtle cursor-pointer"
                            >
                              {syncingZoho === po.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <CloudUpload className="h-4 w-4 mr-2" />
                              )}
                              Sync to Zoho
                            </DropdownMenuItem>
                          )}
                          
                          {/* Delete/Cancel */}
                          {po.status !== 'cancelled' && po.status !== 'completed' && (
                            <>
                              <DropdownMenuSeparator className="bg-wt-border" />
                              <DropdownMenuItem 
                                onClick={() => handleDeletePO(po.id)}
                                className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Cancel PO
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {purchaseOrders.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-wt-text-muted mx-auto mb-4" />
            <p className="text-wt-text-muted">No purchase orders found</p>
            <Button onClick={() => setCreateDialogOpen(true)} className="mt-4 bg-wt-gold hover:bg-wt-gold-hover text-black">
              Create Your First PO
            </Button>
          </div>
        )}
      </div>

      {/* Create PO Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Create Purchase Order</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* PO Header */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Vendor *</Label>
                <Select value={formData.vendor_id} onValueChange={(v) => setFormData({...formData, vendor_id: v})}>
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border max-h-[200px]">
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.vendor_name} ({v.vendor_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">PO Type</Label>
                <Select value={formData.po_type} onValueChange={(v) => setFormData({...formData, po_type: v})}>
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    {PO_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Fulfillment Method</Label>
                <Select value={formData.fulfillment_method} onValueChange={(v) => setFormData({...formData, fulfillment_method: v})}>
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    {FULFILLMENT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Warehouse Location</Label>
                <Select value={formData.warehouse_location_id} onValueChange={(v) => setFormData({...formData, warehouse_location_id: v})}>
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    {locations.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Expected Delivery Date</Label>
                <DateInput
                  value={formData.expected_delivery_date}
                  onChange={(e) => setFormData({...formData, expected_delivery_date: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="border-t border-wt-border pt-4">
              <h3 className="text-lg font-medium text-wt-text-primary mb-4">Line Items</h3>
              
              {/* SKU Search with autocomplete */}
              <div className="mb-4 relative">
                <Label className="text-wt-text-muted text-xs mb-1">Search by SKU or Product Name</Label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                    <Input
                      placeholder="Type SKU or product name..."
                      value={skuSearch}
                      onChange={(e) => {
                        setSkuSearch(e.target.value);
                        searchProductsBySKU(e.target.value);
                      }}
                      className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                    {searchingProducts && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-wt-gold animate-spin" />
                    )}
                  </div>
                </div>
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((item) => (
                      <div
                        key={item.sku}
                        onClick={() => selectProductFromSearch(item)}
                        className="p-3 hover:bg-wt-bg cursor-pointer border-b border-wt-border last:border-b-0"
                      >
                        <div className="flex items-center gap-3">
                          {item.image_url ? (
                            <img src={item.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <div className="w-10 h-10 bg-wt-bg rounded flex items-center justify-center">
                              <Package className="h-5 w-5 text-wt-text-muted" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-wt-gold">{item.sku}</div>
                            <div className="text-xs text-wt-text-primary truncate">{item.name}</div>
                            <div className="text-xs text-wt-text-muted">Stock: {item.current_stock}</div>
                          </div>
                          <div className="text-right">
                            {item.lowest_vendor ? (
                              <div>
                                <div className="text-xs text-wt-text-muted">Best price:</div>
                                <div className="text-sm font-medium text-green-400">{formatCurrency(item.lowest_vendor.cost_price)}</div>
                                <div className="text-xs text-wt-text-muted">{item.lowest_vendor.vendor_name}</div>
                              </div>
                            ) : (
                              <div className="text-sm text-wt-text-muted">{formatCurrency(item.cost_price)}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected Product with Price Comparison */}
              {newLineItem.sku && (
                <div className="mb-4 p-3 bg-wt-bg rounded-lg border border-wt-border">
                  <div className="flex items-center gap-3 mb-3">
                    {newLineItem.image_url ? (
                      <ImageLightbox 
                        src={newLineItem.image_url} 
                        alt={newLineItem.product_title}
                        className="w-12 h-12"
                        thumbnailClassName="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-wt-subtle rounded flex items-center justify-center">
                        <Package className="h-6 w-6 text-wt-text-muted" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-wt-gold">{newLineItem.sku}</div>
                      <div className="text-sm text-wt-text-primary">{newLineItem.product_title}</div>
                    </div>
                  </div>
                  
                  {/* Vendor Price Comparison — May 2026 upgrade:
                      cheapest vendor highlighted in gold, every other
                      vendor labelled with the % they're charging above
                      the cheapest, and a tooltip on each chip showing
                      last PO number / quantity / date so the buyer
                      knows the chip price is recent and not a stale
                      mapping. */}
                  {showPriceComparison && selectedProductVendorPrices.length > 0 && (() => {
                    const valid = selectedProductVendorPrices.filter(v => Number(v.cost_price) > 0);
                    if (valid.length === 0) return null;
                    const minCost = Math.min(...valid.map(v => Number(v.cost_price)));
                    const sorted = [...selectedProductVendorPrices].sort(
                      (a, b) => (Number(a.cost_price) || Infinity) - (Number(b.cost_price) || Infinity)
                    );
                    return (
                      <div className="mb-3" data-testid="vendor-price-comparison-chips">
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-wt-text-muted text-xs">Vendor Price Comparison</Label>
                          <span className="text-[10px] text-wt-text-muted">
                            cheapest highlighted • % shows premium over cheapest
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {sorted.map((vp, idx) => {
                            const cost = Number(vp.cost_price) || 0;
                            const isCheapest = cost > 0 && cost === minCost;
                            const deltaPct = cost > 0 && minCost > 0 && !isCheapest
                              ? Math.round(((cost - minCost) / minCost) * 1000) / 10
                              : null;
                            const isSelected = Number(newLineItem.unit_cost) === cost;
                            const lastSeen = vp.last_seen_at ? new Date(vp.last_seen_at) : null;
                            const ageDays = lastSeen
                              ? Math.round((Date.now() - lastSeen.getTime()) / 86400000)
                              : null;
                            const ageLabel = ageDays === null
                              ? ''
                              : ageDays === 0 ? 'today'
                              : ageDays === 1 ? '1 day ago'
                              : ageDays < 30 ? `${ageDays} days ago`
                              : ageDays < 60 ? `${Math.round(ageDays / 7)} wk ago`
                              : `${Math.round(ageDays / 30)} mo ago`;
                            const title = [
                              vp.last_po_number ? `Last PO: ${vp.last_po_number}` : 'No recent PO',
                              vp.last_qty != null ? `Qty: ${vp.last_qty}` : null,
                              lastSeen ? `Seen: ${ageLabel}` : null,
                              vp.lead_time_days != null ? `Lead time: ${vp.lead_time_days}d` : null,
                            ].filter(Boolean).join(' · ');
                            return (
                              <button
                                key={idx}
                                onClick={() => setNewLineItem({ ...newLineItem, unit_cost: cost })}
                                title={title}
                                data-testid={`vendor-price-chip-${vp.vendor_id || idx}`}
                                className={`px-2.5 py-1.5 rounded text-xs border flex items-center gap-1.5 transition-all ${
                                  isSelected
                                    ? 'border-wt-gold bg-wt-gold/30 text-wt-gold ring-1 ring-wt-gold'
                                    : isCheapest
                                    ? 'border-emerald-400/60 bg-emerald-500/10 text-emerald-300 hover:border-emerald-400'
                                    : 'border-wt-border text-wt-text-secondary hover:border-wt-gold'
                                }`}
                              >
                                {isCheapest && <span className="text-[10px]">★</span>}
                                <span className="font-medium">{vp.vendor_name}</span>
                                <span>{formatCurrency(cost)}</span>
                                {deltaPct !== null && deltaPct > 0 && (
                                  <span className={`text-[10px] font-semibold ${
                                    deltaPct >= 20 ? 'text-rose-400'
                                    : deltaPct >= 10 ? 'text-amber-300'
                                    : 'text-wt-text-muted'
                                  }`}>
                                    +{deltaPct}%
                                  </span>
                                )}
                                {ageLabel && (
                                  <span className="text-[10px] text-wt-text-muted/70">
                                    · {ageLabel}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Quantity and Cost inputs */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-wt-text-muted text-xs">Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newLineItem.quantity}
                        onChange={(e) => setNewLineItem({...newLineItem, quantity: parseInt(e.target.value) || 1})}
                        className="bg-wt-subtle border-wt-border text-wt-text-primary"
                      />
                    </div>
                    <div>
                      <Label className="text-wt-text-muted text-xs">Unit Cost</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newLineItem.unit_cost}
                        onChange={(e) => setNewLineItem({...newLineItem, unit_cost: parseFloat(e.target.value) || 0})}
                        className="bg-wt-subtle border-wt-border text-wt-text-primary"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={addLineItem} className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black">
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Manual Entry (when no product selected) */}
              {!newLineItem.sku && (
                <div className="grid grid-cols-5 gap-2 mb-4">
                  <div>
                    <Input
                      placeholder="SKU"
                      value={newLineItem.sku}
                      onChange={(e) => setNewLineItem({...newLineItem, sku: e.target.value})}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      placeholder="Product Title"
                      value={newLineItem.product_title}
                      onChange={(e) => setNewLineItem({...newLineItem, product_title: e.target.value})}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Qty"
                      type="number"
                      min="1"
                      value={newLineItem.quantity}
                      onChange={(e) => setNewLineItem({...newLineItem, quantity: parseInt(e.target.value) || 1})}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Unit Cost"
                      type="number"
                      step="0.01"
                      value={newLineItem.unit_cost}
                      onChange={(e) => setNewLineItem({...newLineItem, unit_cost: parseFloat(e.target.value) || 0})}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                </div>
              )}
              
              {!newLineItem.sku && (
                <div className="mb-4">
                  <Button 
                    onClick={addLineItem} 
                    disabled={!newLineItem.sku && !newLineItem.product_title}
                    className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Line Item
                  </Button>
                </div>
              )}

              {/* Line Items Table */}
              {formData.line_items.length > 0 && (
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-wt-subtle">
                      <tr>
                        <th className="text-left p-2 text-wt-text-muted text-sm w-16">Image</th>
                        <th className="text-left p-2 text-wt-text-muted text-sm">SKU</th>
                        <th className="text-left p-2 text-wt-text-muted text-sm">Product</th>
                        <th className="text-center p-2 text-wt-text-muted text-sm">Qty</th>
                        <th className="text-right p-2 text-wt-text-muted text-sm">Unit Cost</th>
                        <th className="text-right p-2 text-wt-text-muted text-sm">Total</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.line_items.map((item) => (
                        <tr key={item.id} className="border-t border-wt-border/50">
                          <td className="p-2">
                            {item.image_url ? (
                              <ImageLightbox 
                                src={item.image_url} 
                                alt={item.product_title}
                                className="w-10 h-10"
                                thumbnailClassName="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-wt-bg rounded flex items-center justify-center">
                                <Package className="h-5 w-5 text-wt-text-muted" />
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-wt-gold text-sm">{item.sku}</td>
                          <td className="p-2 text-wt-text-primary text-sm">
                            <div>{item.product_title}</div>
                            {variantLabel(item.variant_title) && (
                              <div className="text-xs text-wt-text-muted" data-testid="po-create-item-variant">{item.variant_title}</div>
                            )}
                          </td>
                          <td className="p-2 text-center text-wt-text-primary">{item.quantity}</td>
                          <td className="p-2 text-right text-wt-text-primary">{formatCurrency(item.unit_cost)}</td>
                          <td className="p-2 text-right text-wt-text-primary font-medium">{formatCurrency(item.quantity * item.unit_cost)}</td>
                          <td className="p-2">
                            <Button size="sm" variant="ghost" onClick={() => removeLineItem(item.id)} className="text-red-400">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-wt-subtle">
                      <tr>
                        <td colSpan="5" className="p-2 text-right text-wt-text-primary font-medium">Total:</td>
                        <td className="p-2 text-right text-wt-gold font-bold">{formatCurrency(calculateTotal())}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Tax Settings */}
            <div className="border-t border-wt-border pt-4">
              <h4 className="text-sm font-medium text-wt-text-primary mb-3">Tax Settings</h4>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <Switch
                    id="tax-inclusive"
                    checked={formData.tax_inclusive}
                    onCheckedChange={(checked) => setFormData({...formData, tax_inclusive: checked})}
                  />
                  <Label htmlFor="tax-inclusive" className="text-wt-text-secondary text-sm">
                    {formData.tax_inclusive ? 'Tax Inclusive' : 'Tax Exclusive'}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-wt-text-secondary text-sm">Tax Rate:</Label>
                  <Input
                    type="number"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})}
                    className="w-20 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <span className="text-wt-text-muted text-sm">%</span>
                </div>
              </div>
              <p className="text-xs text-wt-text-muted mt-2">
                {formData.tax_inclusive 
                  ? 'Prices already include tax. Tax will be extracted from line item totals.' 
                  : 'Tax will be added on top of line item totals.'}
              </p>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-2 gap-4 border-t border-wt-border pt-4">
              <div>
                <Label className="text-wt-text-primary">Internal Notes</Label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => setFormData({...formData, internal_notes: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  rows={3}
                  placeholder="Internal notes (not visible to vendor)"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Vendor Notes</Label>
                <Textarea
                  value={formData.vendor_notes}
                  onChange={(e) => setFormData({...formData, vendor_notes: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  rows={3}
                  placeholder="Notes visible to vendor"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
              <Button variant="ghost" onClick={() => setCreateDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleCreatePO} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                Create Purchase Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <FileText className="h-5 w-5 text-wt-gold" />
              {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPO && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-wt-subtle rounded-lg">
                <div>
                  <p className="text-sm text-wt-text-muted">Vendor</p>
                  <p className="text-wt-text-primary font-medium">{selectedPO.vendor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Status</p>
                  {getStatusBadge(selectedPO.status)}
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Type</p>
                  <p className="text-wt-text-primary">{PO_TYPES.find(t => t.value === selectedPO.po_type)?.label}</p>
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Expected Delivery</p>
                  <p className="text-wt-text-primary">{selectedPO.expected_delivery_date || 'Not set'}</p>
                </div>
                {(selectedPO.vendor_marked_ready_at || selectedPO.vendor_marked_delivered_at) && selectedPO.created_at && (
                  <div data-testid="admin-po-fulfillment-time">
                    <p className="text-sm text-wt-text-muted">Vendor Fulfilment Time</p>
                    <p className="text-emerald-400 font-medium">
                      {(() => {
                        const start = new Date(selectedPO.created_at);
                        const endIso = selectedPO.vendor_marked_delivered_at || selectedPO.actual_delivery_date || selectedPO.vendor_marked_ready_at;
                        const end = new Date(endIso);
                        const hrs = (end - start) / 3_600_000;
                        if (!Number.isFinite(hrs) || hrs < 0) return '—';
                        if (hrs < 1) return `${Math.max(1, Math.round(hrs * 60))}m`;
                        const days = Math.floor(hrs / 24);
                        const remH = Math.round(hrs % 24);
                        if (days >= 1 && remH > 0) return `${days}d ${remH}h`;
                        if (days >= 1) return `${days}d`;
                        return `${Math.round(hrs)}h`;
                      })()}
                    </p>
                  </div>
                )}
                {/* Show all linked orders - Clickable */}
                {(selectedPO.linked_orders?.length > 0 || selectedPO.linked_order_numbers?.length > 0 || selectedPO.linked_order_number) && (
                  <div>
                    <p className="text-sm text-wt-text-muted">Linked Orders</p>
                    <div className="flex flex-wrap gap-1">
                      {(selectedPO.linked_orders || selectedPO.linked_order_numbers || [selectedPO.linked_order_number]).filter(Boolean).map((orderNum, idx) => (
                        <Badge 
                          key={idx} 
                          className="bg-wt-gold/20 text-wt-gold border-0 cursor-pointer hover:bg-wt-gold/30 transition-colors"
                          onClick={() => {
                            setViewDialogOpen(false);
                            navigate(`/orders?orderNumber=${encodeURIComponent(orderNum)}`);
                          }}
                        >
                          {orderNum}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm text-wt-text-muted">Total Amount</p>
                  <div className="flex items-center gap-2">
                    <p className="text-wt-gold font-bold text-lg">{formatCurrency(selectedPO.total_amount)}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRecalculateTotal(selectedPO.id)}
                      className="h-6 px-2 text-xs text-wt-text-muted hover:text-wt-gold"
                      title="Recalculate total from line items"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  </div>
                  {/* Tax Info */}
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={`text-xs ${selectedPO.tax_inclusive ? 'border-green-500/50 text-green-400' : 'border-blue-500/50 text-blue-400'}`}>
                      {selectedPO.tax_inclusive ? 'Tax Incl.' : 'Tax Excl.'}
                    </Badge>
                    <span className="text-xs text-wt-text-muted">
                      {selectedPO.tax_rate || 5}% VAT = {formatCurrency(selectedPO.tax_amount || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tax Toggle for Existing PO */}
              {(selectedPO.status === 'draft' || (isAdmin && !['cancelled', 'completed'].includes(selectedPO.status))) && (
                <div className="flex items-center gap-4 p-3 bg-wt-subtle rounded-lg">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="view-tax-inclusive"
                      checked={selectedPO.tax_inclusive || false}
                      onCheckedChange={(checked) => {
                        handleRecalculateTotal(selectedPO.id, { tax_inclusive: checked, tax_rate: selectedPO.tax_rate || 5 });
                      }}
                    />
                    <Label htmlFor="view-tax-inclusive" className="text-wt-text-secondary text-sm">
                      {selectedPO.tax_inclusive ? 'Tax Inclusive' : 'Tax Exclusive'}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-wt-text-muted text-sm">Tax Rate:</Label>
                    <Input
                      type="number"
                      value={selectedPO.tax_rate || 5}
                      onChange={(e) => {
                        const newRate = parseFloat(e.target.value) || 0;
                        handleRecalculateTotal(selectedPO.id, { tax_rate: newRate, tax_inclusive: selectedPO.tax_inclusive || false });
                      }}
                      className="w-16 h-7 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                      min="0"
                      max="100"
                      step="0.5"
                    />
                    <span className="text-wt-text-muted text-sm">%</span>
                  </div>
                </div>
              )}

              {/* Line Items */}
              <div>
                <h3 className="text-wt-text-primary font-medium mb-2">Line Items</h3>
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-wt-subtle">
                      <tr>
                        <th className="text-left p-2 text-wt-text-muted text-sm w-16">Image</th>
                        <th className="text-left p-2 text-wt-text-muted text-sm">SKU</th>
                        <th className="text-left p-2 text-wt-text-muted text-sm">Product</th>
                        <th className="text-center p-2 text-wt-text-muted text-sm">Qty</th>
                        <th className="text-right p-2 text-wt-text-muted text-sm">Unit Cost</th>
                        <th className="text-right p-2 text-wt-text-muted text-sm">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPO.line_items?.map((item, i) => (
                        <tr key={i} className="border-t border-wt-border/50">
                          <td className="p-2">
                            {item.image_url ? (
                              <ImageLightbox
                                src={item.image_url}
                                alt={item.product_title}
                                className="w-12 h-12"
                                thumbnailClassName="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-wt-subtle rounded flex items-center justify-center">
                                <Package className="h-5 w-5 text-wt-text-muted" />
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-wt-gold text-sm">{item.sku}</td>
                          <td className="p-2 text-wt-text-primary text-sm max-w-[200px]" title={item.product_title}>
                            <div className="truncate">{item.product_title}</div>
                            {variantLabel(item.variant_title) && (
                              <div className="text-xs text-wt-text-muted truncate" data-testid="po-detail-item-variant">{item.variant_title}</div>
                            )}
                          </td>
                          <td className="p-2 text-center text-wt-text-primary">{item.quantity}</td>
                          <td className="p-2 text-right text-wt-text-primary">{formatCurrency(item.unit_cost || 0)}</td>
                          <td className="p-2 text-right text-wt-text-primary font-medium">
                            {formatCurrency(item.total_cost || (item.unit_cost * item.quantity) || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Vendor Proof Images — uploaded by vendor before marking ready */}
              {selectedPO.vendor_ready_images?.length > 0 && (
                <div className="border-t border-wt-border pt-4" data-testid="admin-po-vendor-proof-section">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-wt-text-primary font-medium flex items-center gap-2">
                      <Camera className="h-4 w-4 text-emerald-400" />
                      Vendor Proof Images
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 ml-1">
                        {selectedPO.vendor_ready_images.length}
                      </Badge>
                    </h3>
                    {selectedPO.vendor_marked_ready_at && (
                      <span className="text-xs text-wt-text-muted">
                        Uploaded by vendor before "Mark Ready"
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {selectedPO.vendor_ready_images.map((url, i) => {
                      const fullUrl = url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
                      return (
                        <ImageLightbox
                          key={i}
                          src={fullUrl}
                          alt={`Vendor proof ${i + 1}`}
                          className="w-full aspect-square"
                          thumbnailClassName="w-full h-full object-cover rounded-lg border border-wt-border bg-wt-subtle"
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Vendor Invoice */}
              <div className="border-t border-wt-border pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-wt-text-primary font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4 text-yellow-400" />
                    Vendor Invoice
                  </h3>
                  {selectedPO?.status !== 'draft' && selectedPO?.status !== 'cancelled' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setViewDialogOpen(false); openInvoiceDialog(selectedPO); }}
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      data-testid="view-dialog-upload-invoice-btn"
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      {selectedPO?.vendor_invoice_files?.length > 0 ? 'Add More' : 'Upload'}
                    </Button>
                  )}
                </div>
                {selectedPO?.vendor_invoice_number && (
                  <p className="text-sm text-wt-text-muted mb-2">
                    Invoice #{selectedPO.vendor_invoice_number}
                    {selectedPO?.vendor_invoice_date && <span> • {selectedPO.vendor_invoice_date}</span>}
                  </p>
                )}
                {selectedPO?.vendor_invoice_files?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedPO.vendor_invoice_files.map((file, i) => (
                      <a
                        key={i}
                        href={`${process.env.REACT_APP_BACKEND_URL}${file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-wt-subtle rounded-lg hover:bg-wt-border text-wt-text-primary"
                      >
                        <FileText className="h-4 w-4 text-yellow-400" />
                        Invoice {i + 1}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-wt-text-muted italic">No invoice uploaded yet</p>
                )}
              </div>

              {/* Payment History Section */}
              <div className="border-t border-wt-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-wt-text-primary font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-green-400" />
                    Payment History
                  </h3>
                  {selectedPO?.vendor_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setViewDialogOpen(false);
                        navigate(`/vendors/${selectedPO.vendor_id}`);
                      }}
                      className="text-wt-text-muted hover:text-wt-gold text-xs"
                    >
                      View Vendor Ledger
                    </Button>
                  )}
                </div>
                
                {/* Payment Summary */}
                {paymentSummary && (
                  <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-wt-subtle rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-wt-text-muted">Total</p>
                      <p className="text-wt-text-primary font-semibold">{formatCurrency(paymentSummary.total_amount)}</p>
                    </div>
                    <div className="text-center border-x border-wt-border">
                      <p className="text-xs text-wt-text-muted">Paid</p>
                      <p className="text-green-400 font-semibold">{formatCurrency(paymentSummary.total_paid)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-wt-text-muted">Balance</p>
                      <p className={`font-semibold ${paymentSummary.remaining > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {formatCurrency(paymentSummary.remaining)}
                      </p>
                    </div>
                  </div>
                )}
                
                {loadingPayments ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-wt-text-muted" />
                  </div>
                ) : paymentHistory.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {paymentHistory.map((payment, i) => (
                      <div key={payment.id || i} className="flex items-center justify-between p-3 bg-wt-subtle/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded ${
                            payment.entry_type === 'payment' ? 'bg-green-500/10' : 
                            payment.entry_type === 'advance' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                          }`}>
                            <CreditCard className={`h-4 w-4 ${
                              payment.entry_type === 'payment' ? 'text-green-400' : 
                              payment.entry_type === 'advance' ? 'text-purple-400' : 'text-blue-400'
                            }`} />
                          </div>
                          <div>
                            <p className="text-wt-text-primary text-sm font-medium">
                              {payment.reference_number || payment.transaction_id || 'Payment'}
                            </p>
                            <p className="text-wt-text-muted text-xs">
                              {payment.payment_date} • {payment.payment_mode?.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-semibold">{formatCurrency(payment.amount)}</p>
                          <Badge className={`text-xs border-0 ${
                            payment.entry_type === 'payment' ? 'bg-green-500/20 text-green-400' : 
                            payment.entry_type === 'advance' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {payment.entry_type}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-wt-text-muted italic text-center py-4">No payments recorded yet</p>
                )}
              </div>

              {/* Audit Log Section */}
              <div className="border-t border-wt-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-wt-text-primary font-medium flex items-center gap-2">
                    <History className="h-4 w-4 text-blue-400" />
                    Activity Log
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate('/audit-log')}
                    className="text-wt-text-muted hover:text-wt-gold text-xs"
                  >
                    View All
                  </Button>
                </div>
                {loadingAuditLogs ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-wt-text-muted" />
                  </div>
                ) : auditLogs.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {auditLogs.map((log, i) => {
                      // Determine icon and color based on action type
                      const actionConfig = {
                        created: { icon: Plus, color: 'bg-green-500/10 text-green-400' },
                        status_changed: { icon: RefreshCw, color: 'bg-blue-500/10 text-blue-400' },
                        grn_submitted: { icon: ClipboardCheck, color: 'bg-teal-500/10 text-teal-400' },
                        pickup_assigned: { icon: Truck, color: 'bg-orange-500/10 text-orange-400' },
                        pickup_status_updated: { icon: Truck, color: 'bg-orange-500/10 text-orange-400' },
                        payment_recorded: { icon: CreditCard, color: 'bg-green-500/10 text-green-400' },
                        share_link_created: { icon: Share2, color: 'bg-purple-500/10 text-purple-400' },
                        zoho_synced: { icon: CloudUpload, color: 'bg-purple-500/10 text-purple-400' },
                        zoho_sync_failed: { icon: AlertTriangle, color: 'bg-red-500/10 text-red-400' },
                      };
                      const config = actionConfig[log.action] || { icon: History, color: 'bg-blue-500/10 text-blue-400' };
                      const IconComponent = config.icon;
                      
                      return (
                        <div key={log.id || i} className="flex items-start gap-3 p-2 bg-wt-subtle/50 rounded-lg text-sm">
                          <div className={`p-1 rounded ${config.color.split(' ')[0]}`}>
                            <IconComponent className={`h-3 w-3 ${config.color.split(' ')[1]}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-wt-text-primary">{log.details}</p>
                            <p className="text-wt-text-muted text-xs">
                              {log.user_name ? <span className="text-wt-gold">by {log.user_name} • </span> : ''}
                              {new Date(log.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-wt-text-muted italic">No activity recorded yet</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
                <Button variant="ghost" onClick={() => setViewDialogOpen(false)} className="text-wt-text-secondary">
                  Close
                </Button>
                {/* Zoho Sync Button in View Dialog */}
                {zohoStatus?.configured && !selectedPO?.zoho_synced && selectedPO?.status !== 'draft' && selectedPO?.status !== 'cancelled' && (
                  <Button 
                    onClick={() => { handleSyncToZoho(selectedPO.id); }}
                    disabled={syncingZoho === selectedPO?.id}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {syncingZoho === selectedPO?.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CloudUpload className="h-4 w-4 mr-2" />
                    )}
                    Create in Zoho
                  </Button>
                )}
                {selectedPO?.zoho_synced && (
                  <Badge className="bg-purple-500/20 text-purple-400 border-0 px-4 py-2">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Synced: {selectedPO?.zoho_po_number}
                  </Badge>
                )}
                {['sent', 'confirmed', 'partially_received'].includes(selectedPO?.status) && (
                  <Button 
                    onClick={() => { setViewDialogOpen(false); navigate(`/purchase-orders/${selectedPO.id}/grn`); }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <ClipboardCheck className="h-4 w-4 mr-2" />
                    Receive Goods
                  </Button>
                )}
                <Button onClick={() => handleGenerateShareLink(selectedPO)} variant="outline" className="border-green-500 text-green-400">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Share2 className="h-5 w-5 text-green-400" />
              Share Purchase Order
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-wt-subtle rounded-lg">
              <p className="text-sm text-wt-text-muted">PO Number</p>
              <p className="text-wt-gold font-semibold">{selectedPO?.po_number}</p>
            </div>
            
            <div>
              <Label className="text-wt-text-primary">Share Link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={shareLink}
                  readOnly
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <Button onClick={handleCopyLink} variant="outline" className="border-wt-border">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-wt-text-muted mt-1">This link shows PO without pricing information</p>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={handleShareViaWhatsApp} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                WhatsApp
              </Button>
              <Button onClick={handleNativeShare} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                Share
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pickup Assignment Dialog */}
      <Dialog open={pickupAssignDialogOpen} onOpenChange={setPickupAssignDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Truck className="h-5 w-5 text-orange-400" />
              Assign Pickup Driver
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* PO Info */}
            <div className="p-3 bg-wt-subtle rounded-lg">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-wt-text-muted">PO Number</p>
                  <p className="text-wt-gold font-semibold">{selectedPO?.po_number}</p>
                </div>
                <div>
                  <p className="text-xs text-wt-text-muted">Vendor</p>
                  <p className="text-wt-text-primary font-medium">{selectedPO?.vendor_name}</p>
                </div>
              </div>
            </div>
            
            {/* Driver Selection */}
            <div>
              <Label className="text-wt-text-primary">Select Driver *</Label>
              <Select 
                value={pickupForm.driver_id} 
                onValueChange={(v) => setPickupForm({...pickupForm, driver_id: v})}
              >
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="pickup-driver-select">
                  <SelectValue placeholder="Choose a driver" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4 text-wt-text-muted" />
                        {driver.full_name || driver.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Pickup Date *</Label>
                <DateInput
                  value={pickupForm.pickup_date}
                  onChange={(e) => setPickupForm({...pickupForm, pickup_date: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="pickup-date-input"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Pickup Time</Label>
                <TimeInput
                  value={pickupForm.pickup_time}
                  onChange={(e) => setPickupForm({...pickupForm, pickup_time: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="pickup-time-input"
                />
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label className="text-wt-text-primary">Notes for Driver</Label>
              <Textarea
                value={pickupForm.pickup_notes}
                onChange={(e) => setPickupForm({...pickupForm, pickup_notes: e.target.value})}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                placeholder="Any special instructions for pickup..."
                rows={2}
                data-testid="pickup-notes-textarea"
              />
            </div>
            
            {/* Current Assignment Info */}
            {selectedPO?.pickup_driver_id && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Currently assigned to: <strong>{selectedPO.pickup_driver_name}</strong>
                </p>
                {selectedPO.pickup_status && (
                  <p className="text-xs text-wt-text-muted mt-1">
                    Status: {PICKUP_STATUSES.find(s => s.value === selectedPO.pickup_status)?.label}
                  </p>
                )}
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => setPickupAssignDialogOpen(false)} 
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssignPickup} 
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="confirm-pickup-assignment"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {selectedPO?.pickup_driver_id ? 'Reassign Driver' : 'Assign Driver'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="invoice-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Upload className="h-5 w-5 text-yellow-400" />
              Upload Vendor Invoice
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* PO Info */}
            <div className="p-3 bg-wt-subtle rounded-lg">
              <p className="text-sm text-wt-text-muted">Purchase Order</p>
              <p className="text-wt-gold font-medium">{selectedPO?.po_number}</p>
              <p className="text-sm text-wt-text-primary mt-1">{selectedPO?.vendor_name}</p>
            </div>
            
            {/* Existing Invoices */}
            {selectedPO?.vendor_invoice_files?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Existing Invoices</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedPO.vendor_invoice_files.map((file, i) => (
                    <a
                      key={i}
                      href={`${process.env.REACT_APP_BACKEND_URL}${file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-wt-subtle rounded-lg hover:bg-wt-border text-wt-text-primary text-sm"
                    >
                      <FileText className="h-4 w-4 text-yellow-400" />
                      Invoice {i + 1}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {/* Invoice Number & Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Invoice Number</Label>
                <Input
                  value={invoiceForm.invoice_number}
                  onChange={(e) => setInvoiceForm({...invoiceForm, invoice_number: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  placeholder="INV-001"
                  data-testid="invoice-number-input"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Invoice Date</Label>
                <DateInput
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm({...invoiceForm, invoice_date: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="invoice-date-input"
                />
              </div>
            </div>
            
            {/* File Upload */}
            <div>
              <Label className="text-wt-text-primary">Invoice File (PDF or Image) *</Label>
              <div className="mt-1 border-2 border-dashed border-wt-border rounded-lg p-4 text-center hover:border-yellow-400/50 transition-colors">
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => setInvoiceFile(e.target.files[0])}
                  className="hidden"
                  id="invoice-file-input"
                  data-testid="invoice-file-input"
                />
                <label htmlFor="invoice-file-input" className="cursor-pointer">
                  {invoiceFile ? (
                    <div className="flex items-center justify-center gap-2 text-yellow-400">
                      <FileText className="h-6 w-6" />
                      <span className="text-sm">{invoiceFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-wt-text-muted">
                      <Upload className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm">Click to select invoice file</p>
                      <p className="text-xs mt-1">PDF or Image (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => setInvoiceDialogOpen(false)} 
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleInvoiceUpload}
                disabled={!invoiceFile || uploadingInvoice}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
                data-testid="upload-invoice-btn"
              >
                {uploadingInvoice ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> Upload Invoice</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge POs Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Merge Purchase Orders</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-wt-text-muted text-sm">
              Select 2 or more POs from the same vendor to merge them into a single PO. 
              Only Draft and Sent POs can be merged.
            </p>
            
            {/* Selected POs */}
            {selectedPOsForMerge.length > 0 && (
              <div className="bg-wt-bg rounded-lg p-3 border border-wt-border">
                <p className="text-sm text-wt-text-muted mb-2">Selected for merge ({selectedPOsForMerge.length}):</p>
                <div className="flex flex-wrap gap-2">
                  {selectedPOsForMerge.map(po => (
                    <Badge 
                      key={po.id} 
                      className="bg-wt-gold/20 text-wt-gold cursor-pointer"
                      onClick={() => togglePOForMerge(po)}
                    >
                      {po.po_number} × 
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* PO List for selection */}
            <div className="border border-wt-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-wt-subtle">
                  <tr>
                    <th className="text-center p-2 text-wt-text-muted text-sm w-10"></th>
                    <th className="text-left p-2 text-wt-text-muted text-sm">PO Number</th>
                    <th className="text-left p-2 text-wt-text-muted text-sm">Vendor</th>
                    <th className="text-center p-2 text-wt-text-muted text-sm">Items</th>
                    <th className="text-right p-2 text-wt-text-muted text-sm">Total</th>
                    <th className="text-center p-2 text-wt-text-muted text-sm">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrders
                    .filter(po => po.status === 'draft' || po.status === 'sent')
                    .map(po => (
                      <tr 
                        key={po.id} 
                        className={`border-t border-wt-border/50 cursor-pointer hover:bg-wt-bg ${
                          selectedPOsForMerge.find(p => p.id === po.id) ? 'bg-wt-gold/10' : ''
                        }`}
                        onClick={() => togglePOForMerge(po)}
                      >
                        <td className="p-2 text-center">
                          <input 
                            type="checkbox" 
                            checked={!!selectedPOsForMerge.find(p => p.id === po.id)}
                            onChange={() => {}}
                            className="rounded border-wt-border"
                          />
                        </td>
                        <td className="p-2 text-wt-gold text-sm">{po.po_number}</td>
                        <td className="p-2 text-wt-text-primary text-sm">{po.vendor_name}</td>
                        <td className="p-2 text-center text-wt-text-primary text-sm">{po.line_items?.length || 0}</td>
                        <td className="p-2 text-right text-wt-text-primary text-sm">{formatCurrency(po.total_amount)}</td>
                        <td className="p-2 text-center">{getStatusBadge(po.status)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setMergeDialogOpen(false);
                  setSelectedPOsForMerge([]);
                }} 
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleMergePOs}
                disabled={selectedPOsForMerge.length < 2 || mergingPOs}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {mergingPOs ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Merging...</>
                ) : (
                  <><Package className="h-4 w-4 mr-2" /> Merge {selectedPOsForMerge.length} POs</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit PO Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-xl flex items-center gap-3">
              <Edit2 className="h-5 w-5 text-wt-gold" />
              Edit Purchase Order - {editingPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          
          {editingPO && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-wt-text-primary text-sm">Vendor</Label>
                  <Input
                    value={editingPO.vendor_name || editingPO.vendor?.name || editingPO.vendorName || 'N/A'}
                    disabled
                    className="mt-1 bg-wt-surface/50 border-wt-border text-wt-text-muted"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-primary text-sm">Expected Delivery Date</Label>
                  <DateInput
                    value={editingPO.expected_delivery_date?.split('T')[0] || editingPO.expected_delivery?.split('T')[0] || ''}
                    onChange={(e) => setEditingPO({...editingPO, expected_delivery_date: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary [color-scheme:dark]"
                  />
                </div>
              </div>

              {/* Fulfillment Method */}
              <div>
                <Label className="text-wt-text-primary text-sm">Fulfillment Method</Label>
                <Select
                  value={editingPO.fulfillment_method || 'vendor_delivers'}
                  onValueChange={(value) => setEditingPO({...editingPO, fulfillment_method: value})}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="vendor_delivers">
                      <span className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-green-400" />
                        Vendor Delivers (Ships to Warehouse)
                      </span>
                    </SelectItem>
                    <SelectItem value="wooden_twist_pickup">
                      <span className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-blue-400" />
                        Pickup from Vendor
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-wt-text-primary text-sm">Line Items</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingPO(prev => ({
                      ...prev,
                      line_items: [...(prev.line_items || []), {
                        product_title: '',
                        sku: '',
                        quantity: 1,
                        unit_cost: 0
                      }]
                    }))}
                    className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Product
                  </Button>
                </div>
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-wt-surface">
                      <tr>
                        <th className="text-left p-3 text-wt-text-muted text-sm">Product</th>
                        <th className="text-center p-3 text-wt-text-muted text-sm w-24">Qty</th>
                        <th className="text-center p-3 text-wt-text-muted text-sm w-32">Unit Cost</th>
                        <th className="text-right p-3 text-wt-text-muted text-sm w-32">Total</th>
                        <th className="w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingPO.line_items?.map((item, index) => (
                        <tr key={index} className="border-t border-wt-border">
                          <td className="p-3">
                            {item.product_title && item.sku ? (
                              <div className="flex items-center gap-2">
                                {item.image_url && (
                                  <img src={item.image_url} alt="" className="w-8 h-8 object-cover rounded" />
                                )}
                                <div>
                                  <div className="text-wt-text-primary text-sm">{item.product_title}</div>
                                  {variantLabel(item.variant_title) && (
                                    <div className="text-wt-text-muted text-xs" data-testid="po-edit-item-variant">{item.variant_title}</div>
                                  )}
                                  <div className="text-wt-text-muted text-xs">{item.sku}</div>
                                </div>
                              </div>
                            ) : (
                              <div className="relative">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                                  <Input
                                    value={addingProductIndex === index ? editSkuSearch : ''}
                                    onChange={(e) => {
                                      setAddingProductIndex(index);
                                      setEditSkuSearch(e.target.value);
                                      searchProductsForEdit(e.target.value);
                                    }}
                                    onFocus={() => setAddingProductIndex(index)}
                                    placeholder="Search by SKU or name..."
                                    className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                                  />
                                  {editSearching && addingProductIndex === index && (
                                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-wt-gold animate-spin" />
                                  )}
                                </div>
                                {/* Search Results Dropdown */}
                                {editSearchResults.length > 0 && addingProductIndex === index && (
                                  <div className="absolute z-50 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {editSearchResults.map((searchItem) => (
                                      <div
                                        key={searchItem.sku}
                                        onClick={() => selectProductForEdit(searchItem, index)}
                                        className="p-2 hover:bg-wt-bg cursor-pointer border-b border-wt-border last:border-b-0"
                                      >
                                        <div className="flex items-center gap-2">
                                          {searchItem.image_url ? (
                                            <img src={searchItem.image_url} alt="" className="w-8 h-8 object-cover rounded" />
                                          ) : (
                                            <div className="w-8 h-8 bg-wt-bg rounded flex items-center justify-center">
                                              <Package className="h-4 w-4 text-wt-text-muted" />
                                            </div>
                                          )}
                                          <div className="flex-1 min-w-0">
                                            <div className="text-xs font-medium text-wt-gold">{searchItem.sku}</div>
                                            <div className="text-xs text-wt-text-primary truncate">{searchItem.name}</div>
                                          </div>
                                          <div className="text-xs text-wt-text-muted">
                                            {formatCurrency(searchItem.lowest_vendor?.cost_price || searchItem.cost_price || 0)}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateEditLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              className="bg-wt-bg border-wt-border text-wt-text-primary text-center w-20"
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_cost}
                              onChange={(e) => updateEditLineItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                              className="bg-wt-bg border-wt-border text-wt-text-primary text-center w-28"
                            />
                          </td>
                          <td className="p-3 text-right text-wt-text-primary">
                            {formatCurrency((item.quantity || 0) * (item.unit_cost || 0))}
                          </td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeEditLineItem(index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-wt-surface">
                      <tr>
                        <td colSpan="3" className="p-3 text-right text-wt-text-primary font-medium">Total:</td>
                        <td className="p-3 text-right text-wt-gold font-bold">
                          {formatCurrency(editingPO.line_items?.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_cost || 0), 0) || 0)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Tax Settings */}
              <div className="border-t border-wt-border pt-4">
                <h4 className="text-sm font-medium text-wt-text-primary mb-3">Tax Settings</h4>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="edit-tax-inclusive"
                      checked={editingPO.tax_inclusive || false}
                      onCheckedChange={(checked) => setEditingPO({...editingPO, tax_inclusive: checked})}
                    />
                    <Label htmlFor="edit-tax-inclusive" className="text-wt-text-secondary text-sm">
                      {editingPO.tax_inclusive ? 'Tax Inclusive' : 'Tax Exclusive'}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-wt-text-secondary text-sm">Tax Rate:</Label>
                    <Input
                      type="number"
                      value={editingPO.tax_rate || 5}
                      onChange={(e) => setEditingPO({...editingPO, tax_rate: parseFloat(e.target.value) || 0})}
                      className="w-20 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                      min="0"
                      max="100"
                      step="0.5"
                    />
                    <span className="text-wt-text-muted text-sm">%</span>
                  </div>
                </div>
                <p className="text-xs text-wt-text-muted mt-2">
                  {editingPO.tax_inclusive 
                    ? 'Prices already include tax. Tax will be extracted from line item totals.' 
                    : 'Tax will be added on top of line item totals.'}
                </p>
              </div>

              {/* Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-wt-text-primary text-sm">Internal Notes</Label>
                  <Textarea
                    value={editingPO.internal_notes || ''}
                    onChange={(e) => setEditingPO({...editingPO, internal_notes: e.target.value})}
                    placeholder="Internal notes (not visible to vendor)"
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary h-24"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-primary text-sm">Vendor Notes</Label>
                  <Textarea
                    value={editingPO.vendor_notes || ''}
                    onChange={(e) => setEditingPO({...editingPO, vendor_notes: e.target.value})}
                    placeholder="Notes visible to vendor"
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary h-24"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setEditDialogOpen(false); setEditingPO(null); }}
              className="border-wt-border text-wt-text-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={savingEdit}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              {savingEdit ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-400" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          
          {selectedPO && (
            <div className="space-y-4 py-4">
              {/* PO Info */}
              <div className="bg-wt-subtle rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-wt-gold font-medium">{selectedPO.po_number}</span>
                  <Badge className={`border-0 ${
                    selectedPO.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                    selectedPO.payment_status === 'partial' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {selectedPO.payment_status || 'Unpaid'}
                  </Badge>
                </div>
                <p className="text-sm text-wt-text-muted">{selectedPO.vendor_name}</p>
                <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                  <div>
                    <p className="text-wt-text-muted">Total</p>
                    <p className="text-wt-text-primary font-medium">{formatCurrency(selectedPO.total_amount || 0)}</p>
                  </div>
                  <div>
                    <p className="text-wt-text-muted">Paid</p>
                    <p className="text-green-400 font-medium">{formatCurrency(selectedPO.paid_amount || 0)}</p>
                  </div>
                  <div>
                    <p className="text-wt-text-muted">Due</p>
                    <p className="text-orange-400 font-medium">
                      {formatCurrency((selectedPO.total_amount || 0) - (selectedPO.paid_amount || 0))}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Payment Form */}
              <div>
                <Label className="text-wt-text-secondary">Amount (AED) *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder="Enter payment amount"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="payment-amount-input"
                />
              </div>
              
              <div>
                <Label className="text-wt-text-secondary">Payment Mode</Label>
                <Select 
                  value={paymentForm.payment_mode} 
                  onValueChange={(v) => setPaymentForm({...paymentForm, payment_mode: v})}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="online">Online Payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-wt-text-secondary">Payment Date</Label>
                <DateInput
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              
              <div>
                <Label className="text-wt-text-secondary">Reference Number</Label>
                <Input
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                  placeholder="Transaction/Reference ID"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              
              <div>
                <Label className="text-wt-text-secondary">Notes</Label>
                <Input
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  placeholder="Payment notes"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setPaymentDialogOpen(false); setSelectedPO(null); }}
              className="border-wt-border text-wt-text-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPayment}
              disabled={savingPayment}
              className="bg-green-500 hover:bg-green-600 text-white"
              data-testid="submit-payment-btn"
            >
              {savingPayment ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Recording...</>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
